import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { generalSettingsSchema, tournamentIdSchema, uidSchema } from '@/core/schemas';
import { useAuth } from '@/app/providers/auth/authContext';
import { activeTournamentId } from '@/features/tournament/useTournament';
import { createTournament } from '@/infrastructure/repositories/tournamentRepository';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { FormField } from '@/shared/ui/FormField';

import { describeError } from '@/features/team/describeError';

import { defaultTournamentSettings } from './tournamentDefaults';

/**
 * The empty-state form the admin sees when the deployment points at a
 * tournament id that has no document yet.
 *
 * Only the four fields a fresh setup needs. The rest — phases, per-round mode,
 * songs — inherits the standard configuration and stays editable under
 * `Admin → Einstellungen` before the start.
 */
export function AdminTournamentSetup() {
  const { session } = useAuth();
  const actorUid = session.status === 'authenticated' ? uidSchema.parse(session.account.uid) : null;

  const currentYear = new Date().getFullYear();
  const [name, setName] = useState(`Ratekauer Feuerwehr-Dart-Cup ${currentYear}`);
  const [date, setDate] = useState(`${currentYear}-09-12`);
  const [boardCount, setBoardCount] = useState('8');
  const [maxTeams, setMaxTeams] = useState('96');
  const [validationError, setValidationError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => {
      if (actorUid === null) throw new Error('Nicht angemeldet');
      const parsed = generalSettingsSchema.safeParse({
        name,
        date,
        boardCount: Number(boardCount),
        maxTeams: Number(maxTeams),
      });
      if (!parsed.success) {
        throw new Error(parsed.error.issues[0]?.message ?? 'Bitte die Eingaben prüfen.');
      }
      return createTournament({
        tournamentId: tournamentIdSchema.parse(activeTournamentId),
        settings: defaultTournamentSettings(parsed.data),
        actorUid,
      });
    },
  });

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setValidationError(null);
    create.mutate();
  };

  const message = validationError ?? describeError(create.error);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold">Turnier anlegen</h2>
        <p className="text-fg-subtle text-sm">
          Für diese Installation ({activeTournamentId}) ist noch kein Turnier angelegt. Trag die
          Eckdaten ein — Phasen, Modus und Einlaufsong-Grenze werden mit Standardwerten befüllt und
          lassen sich später unter „Einstellungen" ändern.
        </p>
      </div>

      {message !== null && <Alert tone="danger">{message}</Alert>}

      <form
        onSubmit={handleSubmit}
        className="border-line bg-surface flex flex-col gap-4 rounded-xl border p-5"
      >
        <FormField
          label="Turniername"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <FormField
          label="Datum"
          type="date"
          value={date}
          onChange={(event) => setDate(event.target.value)}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label="Anzahl Boards"
            type="number"
            min={1}
            value={boardCount}
            onChange={(event) => setBoardCount(event.target.value)}
          />
          <FormField
            label="Maximale Teamanzahl"
            type="number"
            min={2}
            value={maxTeams}
            onChange={(event) => setMaxTeams(event.target.value)}
          />
        </div>

        <div className="flex flex-wrap gap-3">
          <Button type="submit" isLoading={create.isPending}>
            Turnier anlegen
          </Button>
        </div>

        <p className="text-fg-subtle text-sm">
          Danach: <strong>Anmeldung öffnen</strong> auf der Steuerungsseite, damit Kapitäne
          registrieren können.
        </p>
      </form>
    </div>
  );
}
